/**
 * Connections: the people you are connected with (both ways: you see each other's whole profile; remove a
 * connection to end it for both), your requests waiting for an answer (Sent), and requests to connect with you
 * (Requests). Organisations, consortia, calls and labs are followed: the Followed Entities page.
 * Route: /connections/?tab=connections | sent | requests. Backend: /interact/follow-requests/.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, InputAdornment, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';
import FollowRequestList from './FollowRequestList';
import { CONNECTIONS_URL, DECLINE_BUTTON, removeConnection, requestsUrl } from './connectionsApi';

const TABS = [
  ['connections', 'Connections'],
  ['sent', 'Sent'],
  ['requests', 'Requests'],
];
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'connections';
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);
  const { showSnackbar } = useSnackbar();
  const { data: connections, error } = useSWR(CONNECTIONS_URL, fetcher);
  const { data: sent } = useSWR(requestsUrl('sent'), fetcher);
  const { data: requests } = useSWR(requestsUrl('received'), fetcher);
  const counts = { connections: connections?.count ?? 0, sent: sent?.length ?? 0, requests: requests?.length ?? 0 };

  const words = search.trim().toLowerCase();
  const shown = (connections?.results || [])
    .filter((u) => !words || [u.name, u.position].some((v) => (v || '').toLowerCase().includes(words)));

  const remove = async (u) => {
    if (!window.confirm(`Remove your connection with ${u.name}? Your profiles close to each other again.`)) return;
    setBusy(u.id);
    try {
      await removeConnection(u.id);
      showSnackbar('Connection removed', 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'The connection could not be removed.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Connections"
        actions={(
          <TextField size="small" placeholder="Search people…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', sm: 260 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        )}>
        <Tabs value={tab} onChange={(_e, v) => setParams(v === 'connections' ? {} : { tab: v })} variant="scrollable"
          scrollButtons="auto" sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          {TABS.map(([key, label]) => (
            <Tab key={key} value={key} label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {label}
                <Chip label={counts[key]} size="small" color={key === 'requests' && counts.requests ? 'error' : 'default'}
                  sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />
              </Box>
            )} />
          ))}
        </Tabs>
      </PageHeader>

      <Box sx={{ p: 1.5 }}>
        {tab !== 'connections' ? (
          <FollowRequestList box={tab === 'sent' ? 'sent' : 'received'} search={search} />
        ) : error ? (
          <Alert severity="error">Your connections could not be loaded.</Alert>
        ) : !connections ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : shown.length === 0 ? (
          <Alert severity="info">
            {words ? 'Nobody matches this search.'
              : 'No connections yet. Ask to connect with researchers from their profile or the Collaboration Hub.'}
          </Alert>
        ) : (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
            {shown.map((u) => (
              <Box key={u.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2.5, py: 1.5,
                borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
                <Avatar src={u.avatar_url || undefined} onClick={() => navigate(`/profile/${u.id}/`)}
                  sx={{ width: 42, height: 42, cursor: 'pointer', bgcolor: 'primary.avatar', color: '#fff', fontWeight: 600 }}>
                  {(u.name || '?')[0]}
                </Avatar>
                <Box flex={1} minWidth={0} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${u.id}/`)}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }} noWrap>{u.name}</Typography>
                  {u.position && <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>{u.position}</Typography>}
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>Connected since {day(u.since)}</Typography>
                </Box>
                <Button {...DECLINE_BUTTON} disabled={busy === u.id} onClick={() => remove(u)}>Remove</Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}
